import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import cloneDeep from 'lodash/cloneDeep';
import mergeWith from 'lodash/mergeWith';
import { parseRuntimeBlockId } from './parseRuntimeBlockId';

type BlocksState = Record<string, BlockStateUnionType | undefined>;
type GetBlockConfig = (originalBlockId: string) => BlockStateUnionType | undefined;

/**
 * On a write to a runtime instance ID, returns the FULL state object that should be
 * stored under that key. The first write to an untouched instance produces a complete
 * state by deep-merging the parent's original config with the incoming partial — so the
 * stored entry has the same shape as any other materialized block.
 *
 * Returns null when the block ID is not a runtime instance — caller should use the
 * normal setter path.
 */
export function materializeInstanceState(
  blockId: string,
  partial: Partial<BlockStateUnionType>,
  blocksState: BlocksState,
  getBlockConfig: GetBlockConfig,
): BlockStateUnionType | null {
  const parsed = parseRuntimeBlockId(blockId);
  if (!parsed.isInstance) {
    return null;
  }

  const existing = blocksState[blockId];
  const base = existing ?? getBlockConfig(parsed.originalBlockId);

  if (!base) {
    console.error('Parent config missing for runtime instance', blockId);
    // Parent config also missing — fall back to the partial alone, preserving the runtime ID.
    return { ...(partial as BlockStateUnionType), id: blockId };
  }

  // mergeWith customizer: replace arrays wholesale instead of lodash's default
  // index-by-index merge (which would leak stale tail items when a partial write
  // shrinks an array).
  const merged = mergeWith({}, base, partial, (_baseVal, overrideVal) =>
    Array.isArray(overrideVal) ? overrideVal : undefined,
  );
  const isolated = cloneDeep(merged);
  isolated.id = blockId;
  return isolated as BlockStateUnionType;
}
